import { Add } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Fade,
  Icon,
  TextField,
  Typography
} from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import { NivosModal } from 'components'
import {
  DocumentFileViewer,
  DocumentList,
  RecentDocuments
} from 'components/documents'
import { DOCUMENT_FOLDER_ADMIN_PERMISSION_CODES } from 'constants/permissions'
import {
  Operations as DocumentOperations,
  operations as documentOperations
} from 'states/dux/document/operations'
import { Payload as DocumentPayload } from 'states/dux/document/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosFolder } from 'types/nivos-folder'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'
import { hasPermissions } from 'utils/user'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  document: DocumentPayload
  user: UserPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    document: DocumentOperations
    user: UserOperations
  }
}

type AppProps = StateProps & DispatchProps

const DocumentsFC: React.FC<any> = (props: AppProps) => {
  const { layout, document, user, operations } = props

  const AddNewFolder: React.FC = () => {
    const [open, setOpen] = React.useState(false)
    const [name, setName] = React.useState('')
    const [errMessage, setError] = React.useState('')

    const _createNewFolder = (): void => {
      if (name.trim() === '') {
        setError('Name is required!')
        return
      }

      void post('/document/', { name }).then(folder => {
        if (Object.hasOwn(folder, 'id')) {
          operations.document.add(folder)
          setOpen(false)
        } else {
          // Handle error here
        }
      })
    }

    return (
      <>
        <NivosModal
          title={'Add new folder'}
          open={open}
          setOpen={setOpen}
          containerProps={{
            sx: {
              width: 'min-content',
              maxHeight: '95vh',
              overflow: 'auto',
              padding: 2
            }
          }}
        >
          <Box padding={2} minWidth={400}>
            <Box
              display={'flex'}
              alignItems={'center'}
              padding={2}
              justifyContent={'space-between'}
              border={errMessage.trim() !== '' ? 1 : 'none'}
              borderColor={'red'}
              borderRadius={'12px'}
            >
              <Typography>Folder name:</Typography>
              <TextField
                value={name}
                onChange={event => {
                  if (errMessage.trim() !== '') {
                    setError('')
                  }
                  setName(event.currentTarget.value)
                }}
              />
            </Box>
            {errMessage.trim() !== '' && (
              <Typography variant={'body2'} padding={1} color={'red'}>
                <em>{errMessage}</em>
              </Typography>
            )}
            <Box marginLeft={'auto'} width={'fit-content'} padding={2}>
              <Button variant={'contained'} onClick={() => _createNewFolder()}>
                Submit
              </Button>
            </Box>
          </Box>
        </NivosModal>
        <Box
          display={'flex'}
          alignItems={'center'}
          paddingX={2}
          paddingY={3}
          onClick={() => setOpen(true)}
          sx={{
            ':hover': { paddingLeft: 3 },
            cursor: 'pointer',
            transition: '0.5s'
          }}
        >
          <Icon>
            <Add />
          </Icon>
          <Typography>Add new folder</Typography>
        </Box>
      </>
    )
  }

  React.useEffect(() => {
    if (document.data.length < 1) {
      operations.document.loading(true)
      void get('/document/folders/').then((data: NivosFolder[]) => {
        const parsedData = data.map(item => objectifyDateStringFields(item))
        operations.document.set(parsedData)
        operations.document.loading(false)
      })
    }
  }, [])

  return (
    <Fade in={true} timeout={1000}>
      <Box
        display={'flex'}
        flexDirection={'column'}
        width={'100%'}
        paddingTop={4}
        sx={{ transition: '.5s' }}
      >
        <Box flexGrow={1}>
          <RecentDocuments />
          {document.loading && (
            <Typography
              variant={'h4'}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              height={1}
            >
              Loading Documents
              <CircularProgress sx={{ marginLeft: 1 }} />
            </Typography>
          )}
          {!document.loading && (
            <Box padding={2}>
              <DocumentFileViewer />
              <Box padding={2} />
              <Box display={'flex'} flexDirection={'column'} width={1}>
                <Typography variant={'h4'}>
                  <strong>
                    {layout.language.translations.documents.toUpperCase()}
                  </strong>
                </Typography>
                <Divider />
                <Box padding={1} />
                {document.data.map((folder, index) => (
                  <DocumentList
                    key={index}
                    category={folder.name}
                    folder={folder}
                  />
                ))}
                {document.data.length === 0 && (
                  <Box padding={4} margin={'auto'}>
                    <Typography variant="h3">No Data</Typography>
                  </Box>
                )}
                {hasPermissions(
                  user,
                  DOCUMENT_FOLDER_ADMIN_PERMISSION_CODES
                ) && <AddNewFolder />}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Fade>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    document: state.document,
    user: state.user
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  document: documentOperations,
  user: userOperations
})

const Documents = connect(mapStateToProps, mapDispatchToProps)(DocumentsFC)

export default Documents
